import Tooltip, { type TooltipContent, TooltipProvider, TooltipTrigger } from '@cobliteam/colib-ui/Tooltip';
import { type ComponentProps, type HTMLAttributes, type ReactElement, type ReactNode, type Ref, useId, useRef, useState } from 'react';
import type TooltipOnIntentPopup from './TooltipOnIntentPopup';

type TriggerElementProps = HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> };

export interface TooltipOnIntentProps {
  render: ReactElement<TriggerElementProps>;
  children: ReactNode;
  disabled?: boolean;
  triggerProps?: Omit<ComponentProps<typeof TooltipTrigger>, 'render' | 'children'>;
  contentProps?: Omit<ComponentProps<typeof TooltipContent>, 'children'>;
}

export default function TooltipOnIntent({ render, children, disabled, triggerProps, contentProps }: TooltipOnIntentProps) {
  const [Popup, setPopup] = useState<typeof TooltipOnIntentPopup | null>(null);
  const [open, setOpen] = useState(false);
  const pending = useRef(false);
  const generatedId = useId();
  const triggerId = triggerProps?.id ?? generatedId;

  const requestPopup = () => {
    if (disabled || pending.current) return;
    pending.current = true;
    void import('./TooltipOnIntentPopup')
      .then(({ default: Component }) => setPopup(() => Component))
      .catch(() => {
        pending.current = false;
      });
  };

  return (
    <TooltipProvider delay={triggerProps?.delay ?? 0}>
      <Tooltip
        disabled={disabled}
        triggerId={triggerId}
        open={open && !disabled}
        onOpenChange={(nextOpen, details) => {
          if (nextOpen && details.reason === 'trigger-focus' && details.event.target instanceof HTMLElement && !details.event.target.matches(':focus-visible')) {
            details.cancel();
            return;
          }
          setOpen(nextOpen);
          if (nextOpen) requestPopup();
        }}
      >
        {/* Keep the trigger's DOM identity stable when help loads during a click or focus transition. */}
        <TooltipTrigger {...triggerProps} id={triggerId} render={render} />
        {Popup && <Popup contentProps={contentProps}>{children}</Popup>}
      </Tooltip>
    </TooltipProvider>
  );
}
